import React from 'react';
import Styled from 'styled-components';
import {Tooltip} from '@forecast-it/design-system';
import {CustomFieldTooltipText} from './CustomFieldTooltipText';
import {useIsTruncatedHeight, useIsTruncatedWidth} from './useIsTruncatedWidth';

// Admin-typed names can be long; the header wraps to at most two lines and ends in an ellipsis, so one long
// name cannot make the whole header row tall. A div, not a span: display -webkit-box is already block-level
// (identical layout), and the Projects list's sortable-header CSS styles every span in a th (sort arrow,
// margin), which must not catch the label. Both parents are divs, so a div is valid in each: the Projects
// list's HeaderContent, and the design-system SortableHeaderCell (its StyledCell is a div, not a button).
const HeaderLabel = Styled.div`
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	overflow-wrap: anywhere;
`;

// The full name shows in a design-system hover box only while the clamp cuts it off; a name that fits gets
// no hover at all. The box is interactable so a click on the label still reaches the header's sort toggle.
export const HeaderName = ({name}: {name: string}): JSX.Element => {
	const [ref, isTruncated] = useIsTruncatedHeight<HTMLDivElement>();
	const label = (
		<HeaderLabel ref={ref} data-cy="cf-header-label">
			{name}
		</HeaderLabel>
	);
	return isTruncated ? (
		<Tooltip content={<CustomFieldTooltipText>{name}</CustomFieldTooltipText>} interactable>
			{label}
		</Tooltip>
	) : (
		label
	);
};

// One line, cut with an ellipsis. The design-system cell is a nowrap flex row, so a line clamp never
// wraps (nothing to measure by height) and a flex item that cannot shrink below its text never draws an
// ellipsis. Hence nowrap + text-overflow here, min-width: 0 so this box (and the flex wrappers around it -
// the EditableCell trigger and the read-only fill) can shrink to the cell.
const Value = Styled.span`
	display: block;
	flex: 0 1 auto;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

// The full value shows in a design-system hover box, only while the value is cut off, for every user. The
// box is interactable so a click still reaches whatever wraps the value (an inline editor or a row link), and
// the anchor is the clipped value itself, so the box's arrow centres on the text that is shown. Any
// editability hint is the call site's own concern, not this box's (on the Opportunities list, the
// customFieldCell wrapper's native title).
export const TruncatedValue = ({value}: {value: string}): JSX.Element => {
	const [ref, isTruncated] = useIsTruncatedWidth<HTMLSpanElement>();
	const text = (
		<Value ref={ref} data-cy="cf-value">
			{value}
		</Value>
	);
	return isTruncated ? (
		<Tooltip content={<CustomFieldTooltipText>{value}</CustomFieldTooltipText>} interactable>
			{text}
		</Tooltip>
	) : (
		text
	);
};
